import { EDITOR_EPOCH_HEADER } from "@household/shared/floor-plan-headers";
import { copy } from "../app/i18n.js";
import { expectedIdentity, expectedIdentityHeaders } from "./expectedIdentity.js";
import { enqueueMutation, newRequestId } from "./offlineQueue.js";
import { notifySessionExpired } from "./sessionEvents.js";
import { newWriteId, noteWriteSent } from "./writeIdentity.js";

/**
 * Names the write this request performs, so its realtime echo can be matched
 * back to it (#800). Lower-case because `Headers` normalizes, and the API reads
 * it that way.
 */
const WRITE_ID_HEADER = "x-write-id";

/**
 * The header carrying the request id (#798). Lower-case because `Headers`
 * normalizes, and the API reads it that way too.
 *
 * Deliberately separate from the write id above: an idempotency key may only
 * ride a write that is safe to REPLAY, while a write id rides every mutation.
 */
const IDEMPOTENCY_HEADER = "idempotency-key";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Writes that must never be silently queued for later replay: auth/session,
// household membership, and money movements need to fail loudly while offline
// rather than apply minutes later against possibly-changed state. Everything
// else (chores, groceries, inventory, shopping, recipes, ...) is queue-safe.
const MUST_BE_ONLINE_PREFIXES = [
  "/api/v1/auth/",
  "/api/v1/account/",
  // Both spellings are live: /household/ (singular) carries member permissions,
  // invitations, and backup restore — none of which may replay from a queue.
  "/api/v1/household/",
  "/api/v1/households/",
  "/api/v1/invitations/",
  "/api/v1/platform/",
  "/api/v1/finance",
  "/api/v1/accounts",
  "/api/v1/transactions",
  "/api/v1/budgets",
  "/api/v1/recurring",
  "/api/v1/assistant/",
  // Matter/BMS connector lifecycle — commissioning carries live Wi-Fi
  // credentials in the request body, and connector ops act on a live controller,
  // so they must never be serialized into the offline outbox and replayed later.
  "/api/v1/bms/connectors/"
];

export type OfflineQueuedResult = { __offlineQueued: true; id: string };

// Callers that want to react to a queued write (vs. a real server response) can
// narrow the resolved value with this; most callers ignore it and degrade
// gracefully (undefined fields read through `?? []` guards).
export function isOfflineQueued(value: unknown): value is OfflineQueuedResult {
  return typeof value === "object" && value !== null && (value as { __offlineQueued?: unknown }).__offlineQueued === true;
}

/**
 * The marker, refused (#1122).
 *
 * For a caller whose next line READS the response — `result.added`, `result.failed`,
 * or storing it as the row's saved state — the marker is not a degraded success,
 * it is a write that never happened. Taking it at face value is how a sheet
 * retires a row the server has never seen, and how a queue id ends up stored as
 * domain data.
 *
 * It throws the same `ApiError(status 0)` that `mustBeOnline` produces, so a
 * surface has ONE branch for "this write did not reach the server" no matter
 * which way it found out. Those surfaces pair this with `mustBeOnline`, which
 * makes the marker unreachable; the guard is what keeps it unreachable when the
 * option is dropped from one call in a later edit.
 */
export function assertNotOfflineQueued<T>(value: T | OfflineQueuedResult): T {
  if (isOfflineQueued(value)) throw new ApiError(friendlyNetworkMessage(), 0, { networkError: true });
  return value as T;
}

function isQueueableMutation(method: string, path: string, options: RequestInit): boolean {
  if (!MUTATION_METHODS.has(method)) return false;
  // File uploads (FormData) can't be serialized into the JSON outbox; the
  // photo/attachment queue (QueuedPhotoGrid) owns those.
  if (options.body instanceof FormData) return false;
  const pathname = path.split("?")[0] ?? path;
  if (!pathname.startsWith("/api/")) return false;
  return !MUST_BE_ONLINE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function looksLikeHtml(value: string): boolean {
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();
  return lower.startsWith("<!doctype html") || lower.startsWith("<html") || /<body[\s>]/i.test(trimmed);
}

function compactMessage(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > 500 ? `${normalized.slice(0, 497)}...` : normalized;
}

export const friendlyNetworkMessage = (): string =>
  copy("errors.network", "Can't reach the server right now — check your connection and try again.");

/**
 * A failed `fetch()` rejects at the network layer (never reached the server):
 * Firefox throws "NetworkError when attempting to fetch resource", Chrome
 * "Failed to fetch", Safari "Load failed" — all as a plain TypeError. These are
 * usually transient (cold backend/tunnel, a request aborted during initial
 * navigation) and are worth retrying / wording gently rather than surfacing the
 * raw browser string as a hard error (#278).
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 0;
  if (!(error instanceof TypeError)) return false;
  const message = error.message.toLowerCase();
  return message.includes("failed to fetch")
    || message.includes("networkerror")
    || message.includes("load failed")
    || message.includes("network request failed");
}

/**
 * The server could not be reached, or a gateway in front of it answered for
 * it (September audit). A 502/503/504 arrives here only after `api()` has
 * retried it, and it says the same thing a dropped connection says: nothing
 * about the person changed, the request did not get through. The shell's
 * first auth read treats both as "try again", never as "signed out".
 */
export function isServerUnreachableError(error: unknown): boolean {
  if (isTransientNetworkError(error)) return true;
  return error instanceof ApiError && (error.status === 502 || error.status === 503 || error.status === 504);
}

/**
 * A write refused by the public demo instance (#839). The API tags these 403s so
 * the UI can show the demo notice as a calm toast rather than a red failure
 * alert — nothing is broken, the instance just doesn't persist.
 */
export function isDemoModeRejection(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 403) return false;
  const data = error.data;
  if (typeof data !== "object" || data === null) return false;
  return (data as { demoMode?: unknown }).demoMode === true;
}

export function displayErrorMessage(error: unknown, fallback = copy("errors.actionFailed", "Action failed")): string {
  if (isTransientNetworkError(error)) return friendlyNetworkMessage();
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const trimmed = raw.trim();
  if (!trimmed) return fallback;
  if (looksLikeHtml(trimmed)) return copy("errors.htmlErrorPage", "The server returned an HTML error page. Please try again in a minute.");
  return compactMessage(trimmed);
}

function readableErrorMessage(response: Response, data: unknown): string {
  // The last-resort sentence for a failure the server did not word itself: an
  // empty body, a body that is not JSON, or a non-retryable HTML page. It is the
  // one this module produces most often, so it cannot stay a literal (#1088).
  //
  // 502/503/504 pick a different one (#562). Those three are the shape of
  // "nothing on the other side wrote this answer": mid-deploy the tunnel or the
  // reverse proxy replies on behalf of an API that is not running, and what it
  // puts in the body is its own business — an HTML error page, a line of plain
  // text, or nothing at all. The first pass at this only covered the HTML case,
  // so a gateway 503 with an EMPTY body still fell through to the raw
  // "Request failed: 503" this issue is named after.
  //
  // What decides the wording is whether the body carries a message the server
  // worded, not what the body is made of. A JSON `{error}` from this API — the
  // Access gate's own 503, the transcription host's "busy — try again in a
  // moment" — is a real sentence about a real condition and still speaks for
  // itself.
  const unworded = RETRYABLE_GATEWAY_STATUSES.has(response.status)
    ? copy("errors.serverRestarting", "The server is restarting or unreachable. Please try again in a minute.")
    : copy("errors.requestFailed", `Request failed: ${response.status}`, { status: response.status });
  if (!data || typeof data !== "object" || !("error" in data)) return unworded;
  const raw = String((data as { error?: unknown }).error ?? "");
  const trimmed = raw.trim();
  if (!trimmed) return unworded;
  if (looksLikeHtml(trimmed)) return unworded;
  return compactMessage(trimmed);
}

// Gateway statuses seen when the backend/tunnel is still coming up. Safe to retry
// for idempotent reads.
const RETRYABLE_GATEWAY_STATUSES = new Set([502, 503, 504]);
const MAX_TRANSIENT_RETRIES = 2;
const RETRY_BACKOFF_MS = 400;

function isIdempotentMethod(method: string | undefined): boolean {
  const normalized = (method ?? "GET").toUpperCase();
  return normalized === "GET" || normalized === "HEAD";
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type ApiRequestOptions = RequestInit & {
  /** Skip the offline outbox for this mutation: fail loudly on network errors
   * instead of queueing a replay. Callers that own their own retry/merge
   * strategy (e.g. the floor-plan editor's latest-state sync queue, where a
   * stale replayed absolute geometry could clobber newer edits) set this. */
  mustBeOnline?: boolean;
  /** Send a specific request id instead of a fresh one (#798). Set by a caller
   * that keeps its own outbox and is re-sending an entry it already stamped, so
   * the attempt and the replay stay the same request to the server. */
  idempotencyKey?: string;
  /** Observe bootstrap response headers without changing request/response handling. */
  onResponse?: (response: Response) => void;
};

export async function api<T>(path: string, requestOptions: ApiRequestOptions = {}): Promise<T> {
  const { mustBeOnline = false, idempotencyKey, onResponse, ...options } = requestOptions;
  const headers = new Headers(options.headers);
  const hasBody = options.body != null;
  if (hasBody && !(options.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const method = (options.method ?? "GET").toUpperCase();
  // Say which household and person this tab believes it is acting for (#105),
  // captured once so the attempt and any outbox replay name the same pair. The
  // server only compares and logs; it never decides anything with these.
  const identityAtSend = path.startsWith("/api/") ? expectedIdentity() : null;
  for (const [name, value] of Object.entries(expectedIdentityHeaders(identityAtSend))) {
    if (!headers.has(name)) headers.set(name, value);
  }
  // Retry only idempotent reads: a network-layer failure on a POST/PATCH may have
  // reached the server (lost response), so retrying could double-write. Skip
  // retries when the device is known-offline so callers (e.g. the shopping offline
  // cache) fall back immediately instead of waiting out the backoff.
  const canRetry = isIdempotentMethod(method)
    && !(typeof navigator !== "undefined" && navigator.onLine === false);
  let attempt = 0;

  // Name this write and tell the suppression ledger about it BEFORE the request
  // goes out (#800) — the household event can be delivered while the write is
  // still in flight, so an id registered on the response would arrive too late
  // to recognise its own echo.
  //
  // Every mutation, not only the queue-safe ones: `switchHousehold` is a
  // must-be-online write made with `{ refresh: false }`, which is exactly the
  // shape of the uncovered write this issue is about.
  let writeId: string | null = null;
  if (MUTATION_METHODS.has(method) && !headers.has(WRITE_ID_HEADER)) {
    writeId = newWriteId();
    headers.set(WRITE_ID_HEADER, writeId);
    noteWriteSent(writeId);
  }

  // Stamp a request id on the FIRST attempt of any write that could end up in
  // the outbox (#798).
  //
  // The timing is the entire fix. A network-layer failure is precisely the
  // "the server may well have committed, we just never saw the answer" window,
  // and the outbox replays into it. An id minted when the write is *queued*
  // names a request the server never saw on the attempt that may already have
  // applied, so it cannot recognise the replay and the write lands twice. Minted
  // here, both attempts are the same request and the server answers the second
  // from its ledger.
  //
  // Only queue-safe mutations get one: a must-be-online write is never replayed,
  // and a user hitting a button again is a new request, not a retry of this one.
  const queueable = isQueueableMutation(method, path, options);
  const requestId = queueable ? (idempotencyKey ?? newRequestId()) : null;
  if (requestId && !headers.has(IDEMPOTENCY_HEADER)) headers.set(IDEMPOTENCY_HEADER, requestId);
  // When the id first went out, which is what the replay horizon has to measure
  // from (codex review). The server's ledger row is created when this attempt
  // *reaches* it; stamping the outbox entry in the catch below would instead
  // measure from when the failure was noticed, and a request can stall for a
  // long time before it fails — retries above are inside that gap too. Long
  // enough and the entry outlives its ledger row despite looking young.
  const attemptedAt = Date.now();

  for (;;) {
    let response: Response;
    try {
      response = await fetch(path, { ...options, headers, credentials: "include" });
    } catch (error) {
      if (canRetry && isTransientNetworkError(error) && attempt < MAX_TRANSIENT_RETRIES) {
        attempt += 1;
        await delay(RETRY_BACKOFF_MS * attempt);
        continue;
      }
      // fetch throws a TypeError on network failure (offline, DNS, connection
      // reset) — never on an HTTP error status. A queue-safe mutation is stored
      // in the offline outbox and replayed on reconnect; the caller resolves with
      // an "offline queued" marker instead of throwing so the UI degrades
      // gracefully. Reads (retried above) and must-be-online writes fall through.
      if (error instanceof TypeError && !mustBeOnline && queueable) {
        try {
          const queued = await enqueueMutation({
            method,
            url: path,
            body: typeof options.body === "string" ? options.body : null,
            // Carried into the outbox so the replay still names this write
            // (#800); a replayed write with no id produces an echo nothing can
            // attribute, which a live mark is free to swallow.
            ...(writeId ? { writeId } : {}),
            // The id this attempt already sent — carried through so the replay
            // and the possibly-committed attempt are one request (#798).
            ...(requestId ? { idempotencyKey: requestId } : {}),
            // Who the write was made by, and for which household (#105), so the
            // replay can say so even after a sign-out or a household switch.
            ...(identityAtSend?.householdId ? { householdId: identityAtSend.householdId } : {}),
            ...(identityAtSend?.userId ? { userId: identityAtSend.userId } : {}),
            attemptedAt
          });
          return { __offlineQueued: true, id: queued.id } as T;
        } catch {
          // Outbox unavailable (e.g. storage blocked): surface the real failure.
          throw error;
        }
      }
      // Normalize the raw browser TypeError into a friendly, classifiable ApiError
      // (status 0 = network layer) so callers show gentle wording, not "NetworkError…".
      if (isTransientNetworkError(error)) {
        throw new ApiError(friendlyNetworkMessage(), 0, { networkError: true });
      }
      throw error;
    }

    if (canRetry && RETRYABLE_GATEWAY_STATUSES.has(response.status) && attempt < MAX_TRANSIENT_RETRIES) {
      attempt += 1;
      await delay(RETRY_BACKOFF_MS * attempt);
      continue;
    }

    onResponse?.(response);
    const text = await response.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text };
      }
    }

    if (!response.ok) {
      // A 401 on a household request means the session behind this tab is
      // gone (September audit). The auth routes are excluded: there a 401 is
      // the answer to a wrong password, not a lost session.
      if (response.status === 401 && !path.startsWith("/api/v1/auth/")) {
        notifySessionExpired();
        // The caller's own catch usually paints the message it gets, and the
        // server's "Not authenticated" would overwrite the shell's sentence
        // a beat after it was set; hand every caller the same sentence.
        throw new ApiError(copy("auth.sessionEnded", "Your session ended. Sign in to continue."), response.status, data);
      }
      throw new ApiError(readableErrorMessage(response, data), response.status, data);
    }

    return data as T;
  }
}

/**
 * What a caller may set on a JSON mutation. `headers` is here for exactly one
 * kind of request concern: one that has to reach a `DELETE`, where there is no
 * body to carry it. The floor-plan editor epoch (#893) is the case that opened
 * it — see `EDITOR_EPOCH_HEADER` below.
 */
type JsonRequestOptions = Pick<ApiRequestOptions, "mustBeOnline" | "idempotencyKey" | "headers">;

/**
 * The plan generation the Home Map editor captured, sent with every write it
 * makes against a plan's graph (#893).
 *
 * Re-exported from `@household/shared/floor-plan-headers` rather than written
 * out here (#1291), which is the one place this file departs from the
 * `WRITE_ID_HEADER` / `IDEMPOTENCY_HEADER` mirroring convention above. The
 * reason is specific and not general: the #893 fence **fails open**, so a
 * renamed local copy would have quietly stopped fencing every editor write —
 * measured, no unit or API test went red — while a mismatched write id or
 * idempotency key breaks something a test can see. A subpath, never the shared
 * root barrel, which would drag the server surface into the browser bundle.
 */
export { EDITOR_EPOCH_HEADER };

/** The write options for a plan-graph mutation made from an editor session. */
export function editorEpochRequest(editorEpoch: number | null | undefined): JsonRequestOptions {
  // A plan whose detail predates the column, or a caller with no session to
  // quote, sends nothing rather than a fabricated 0 — the server treats an
  // absent header as "not fenced", and inventing an epoch would fence a write
  // against a number nobody agreed on.
  return {
    mustBeOnline: true,
    ...(typeof editorEpoch === "number" ? { headers: { [EDITOR_EPOCH_HEADER]: String(editorEpoch) } } : {})
  };
}

/** True when a 409 says the plan graph was replaced under this editor (#893). */
export function isEditorEpochConflict(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  const data = error.data as { editorEpochConflict?: unknown } | null;
  return data?.editorEpochConflict === true;
}

export function postJson<T>(path: string, body: unknown, options: JsonRequestOptions = {}): Promise<T> {
  return api<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
    ...options
  });
}

/**
 * PUT, which `MUTATION_METHODS` above has always accepted and nothing had a
 * helper for (#1197). The sibling of `patchJson`, not a variant of it: PUT is
 * for a write that states the whole of a small thing rather than a change to
 * part of a larger one — a recall ruling is dismissed or it is not.
 */
export function putJson<T>(path: string, body: unknown, options: JsonRequestOptions = {}): Promise<T> {
  return api<T>(path, {
    method: "PUT",
    body: JSON.stringify(body),
    ...options
  });
}

export function patchJson<T>(path: string, body: unknown, options: JsonRequestOptions = {}): Promise<T> {
  return api<T>(path, {
    method: "PATCH",
    body: JSON.stringify(body),
    ...options
  });
}

export function deleteJson<T>(path: string, options: JsonRequestOptions = {}): Promise<T> {
  return api<T>(path, { method: "DELETE", ...options });
}
